import type { ConnectionState } from '@kontor/shared'
import { create } from 'zustand'

interface ConnectionStore {
  /** Keyed by server id. Fed by the websocket, seeded by the REST snapshot on load. */
  states: Record<string, ConnectionState>
  /** False while the websocket is down, so the UI can say the status may be stale. */
  live: boolean
  apply: (state: ConnectionState) => void
  replaceAll: (states: ConnectionState[]) => void
  setLive: (live: boolean) => void
  clear: () => void
}

export const useConnectionStore = create<ConnectionStore>((set) => ({
  states: {},
  live: false,
  apply: (state) => set((store) => ({ states: { ...store.states, [state.serverId]: state } })),
  replaceAll: (states) =>
    set({ states: Object.fromEntries(states.map((state) => [state.serverId, state])) }),
  setLive: (live) => set({ live }),
  clear: () => set({ states: {}, live: false }),
}))

const DISCONNECTED_SINCE = new Date(0).toISOString()

/** A server the backend has never touched has no entry yet; that is "disconnected", not unknown. */
export function connectionOf(
  states: Record<string, ConnectionState>,
  serverId: string,
): ConnectionState {
  return (
    states[serverId] ?? {
      serverId,
      status: 'disconnected',
      detail: null,
      since: DISCONNECTED_SINCE,
      attempts: 0,
    }
  )
}
